import { Streami18n } from "stream-chat-react";
import type { Language } from "Translations/languageConfig";

const czechTranslations = {
  "Type your message": "Zpráva...",
  Send: "Odeslat",
  "aria/Send": "Odeslat",
  "{{ memberCount }} members": "{{ memberCount }} členů",
  "{{ user }} is typing...": "{{ user }} píše...",
  "{{ users }} and {{ user }} are typing...":
    "{{ users }} a {{ user }} píšou...",
  "{{ users }} and more are typing...": "{{ users }} a další píšou...",
  "New Messages!": "Nové zprávy!",
  "No chats here yet…": "Zatím žádné konverzace…",
  "You have no channels currently": "Zatím nemáte žádné konverzace",
  "Nothing yet...": "Zatím nic...",
  Reply: "Odpovědět",
  "Reply to Message": "Odpovědět na zprávu",
  Thread: "Vlákno",
  // Czech has four plural categories (one/few/many/other); i18next falls back to the bare key
  // ("replyCount") when the category's form is missing, so every count form must be present.
  replyCount_one: "1 odpověď",
  replyCount_few: "{{ count }} odpovědi",
  replyCount_many: "{{ count }} odpovědí",
  replyCount_other: "{{ count }} odpovědí",
  "Thread reply": "Odpověď ve vlákně",
  "Edit Message": "Upravit zprávu",
  Edited: "Upraveno",
  Delete: "Smazat",
  "Message deleted": "Zpráva smazána",
  "This message was deleted...": "Tato zpráva byla smazána...",
  Pin: "Připnout",
  Unpin: "Odepnout",
  "Pinned by": "Připnul/a",
  "Message pinned": "Zpráva připnuta",
  Mute: "Ztlumit",
  Unmute: "Zrušit ztlumení",
  Flag: "Nahlásit",
  Copy: "Kopírovat",
  Quote: "Citovat",
  "Mark as unread": "Označit jako nepřečtené",
  "Messages have been marked unread.": "Zprávy byly označeny jako nepřečtené.",
  Close: "Zavřít",
  Cancel: "Zrušit",
  "Load more": "Načíst více",
  Search: "Hledat",
  "Searching...": "Hledání...",
  "No results found": "Žádné výsledky",
  "Start typing to search": "Začněte psát pro vyhledávání",
  "Attach files": "Připojit soubory",
  "Drag your files here": "Přetáhněte soubory sem",
  Delivered: "Doručeno",
  Sent: "Odesláno",
  "Sending...": "Odesílání...",
  "Error · Unsent": "Chyba · Neodesláno",
  "Message Failed · Click to try again":
    "Zpráva se nepodařila odeslat · Klikněte pro opakování",
  "Send message request failed": "Zprávu se nepodařilo odeslat",
  "Cannot seek in the recording": "V nahrávce nelze přeskakovat",
  "Failed to play the recording": "Nahrávku se nepodařilo přehrát",
  "Recording format is not supported and cannot be reproduced":
    "Formát nahrávky není podporován a nelze ji přehrát",
  "Error reproducing the recording": "Chyba při přehrávání nahrávky",
  "Connection failure, reconnecting now...":
    "Chyba připojení, probíhá opětovné připojení...",
  "Also send in channel": "Odeslat také do kanálu",
  "Also send as a direct message": "Odeslat také do konverzace",
  "{{count}} unread_one": "{{count}} nepřečtená",
  "{{count}} unread_few": "{{count}} nepřečtené",
  "{{count}} unread_many": "{{count}} nepřečtených",
  "{{count}} unread_other": "{{count}} nepřečtených",
  "Unread messages": "Nepřečtené zprávy",
  unreadMessagesSeparatorText_one: "1 nepřečtená zpráva",
  unreadMessagesSeparatorText_few: "{{count}} nepřečtené zprávy",
  unreadMessagesSeparatorText_many: "{{count}} nepřečtených zpráv",
  unreadMessagesSeparatorText_other: "{{count}} nepřečtených zpráv",
  "Latest Messages": "Nejnovější zprávy",
  "Empty message...": "Prázdná zpráva...",
  You: "Vy",
  "{{ watcherCount }} online": "{{ watcherCount }} online",
  "Slow Mode ON": "Pomalý režim zapnut",
  "Error uploading file": "Chyba při nahrávání souboru",
  "Error uploading image": "Chyba při nahrávání obrázku",
  "aria/Close thread": "Zavřít vlákno",
  "aria/Open Thread": "Otevřít vlákno",
  "aria/Emoji picker": "Výběr emoji",
  "aria/Message Options": "Možnosti zprávy",
  "aria/Open Reaction Selector": "Otevřít výběr reakcí",
  "🏙 Attachment...": "🏙 Příloha...",
  "aria/Attachment": "Příloha",
  "aria/Download attachment": "Stáhnout přílohu",
  "aria/Open Attachment Selector": "Otevřít výběr příloh",
  "aria/Remove attachment": "Odebrat přílohu",
  "Download attachment {{ name }}": "Stáhnout přílohu {{ name }}",
  "Error uploading attachment": "Chyba při nahrávání přílohy",
  "Unsupported attachment": "Nepodporovaná příloha",
  "Wait until all attachments have uploaded":
    "Počkejte, než se všechny přílohy nahrají",
  New: "Nové",
  "timestamp/DateSeparator":
    "{{ timestamp | timestampFormatter(calendar: true) }}",
  "timestamp/MessageTimestamp":
    "{{ timestamp | timestampFormatter(calendar: true) }}",
  "timestamp/SystemMessage":
    "{{ timestamp | timestampFormatter(format: dddd L) }}",
  "timestamp/LiveLocation":
    "{{ timestamp | timestampFormatter(calendar: true) }}",
  "timestamp/PollVote":
    "{{ timestamp | timestampFormatter(format: MMM D [at] HH:mm) }}",
  "timestamp/PollVoteTooltip":
    "{{ timestamp | timestampFormatter(calendar: true) }}",
  "timestamp/ReminderNotification":
    "{{ timestamp | timestampFormatter(calendar: true) }}",
};

const appLanguageToStreamLanguage = (language: Language): string => {
  switch (language) {
    case "en":
      return "en";
    case "de":
      return "de";
    default:
      return "cs";
  }
};

const czechDayjsLocale = {
  name: "cs",
  months:
    "leden_únor_březen_duben_květen_červen_červenec_srpen_září_říjen_listopad_prosinec".split(
      "_",
    ),
  monthsShort: "led_úno_bře_dub_kvě_čvn_čvc_srp_zář_říj_lis_pro".split("_"),
  weekdays: "neděle_pondělí_úterý_středa_čtvrtek_pátek_sobota".split("_"),
  weekdaysShort: "ne_po_út_st_čt_pá_so".split("_"),
  weekdaysMin: "ne_po_út_st_čt_pá_so".split("_"),
  weekStart: 1 as const,
  formats: {
    LT: "H:mm",
    LTS: "H:mm:ss",
    L: "DD.MM.YYYY",
    LL: "D. MMMM YYYY",
    LLL: "D. MMMM YYYY H:mm",
    LLLL: "dddd D. MMMM YYYY H:mm",
  },
  calendar: {
    sameDay: "[Dnes v] H:mm",
    nextDay: "[Zítra v] H:mm",
    nextWeek: "dddd [v] H:mm",
    lastDay: "[Včera v] H:mm",
    lastWeek: function lastWeek(this: {
      day(): number;
      format(f: string): string;
    }): string {
      const labels = [
        "[Minulou neděli v] H:mm",
        "[Minulé pondělí v] H:mm",
        "[Minulé úterý v] H:mm",
        "[Minulou středu v] H:mm",
        "[Minulý čtvrtek v] H:mm",
        "[Minulý pátek v] H:mm",
        "[Minulou sobotu v] H:mm",
      ];
      return this.format(labels[this.day()]);
    } as unknown as string,
    sameElse: "D. MMMM YYYY [v] H:mm",
  },
  relativeTime: {
    future: "za %s",
    past: "před %s",
    s: "pár sekund",
    m: "minutu",
    mm: "%d minut",
    h: "hodinu",
    hh: "%d hodin",
    d: "den",
    dd: "%d dní",
    M: "měsíc",
    MM: "%d měsíců",
    y: "rok",
    yy: "%d let",
  },
  ordinal: (n: number) => `${n}.`,
};

export const createStreamChatI18n = (appLanguage: Language) => {
  const streamLanguage = appLanguageToStreamLanguage(appLanguage);

  const i18n = new Streami18n({
    language: streamLanguage,
    ...(streamLanguage === "cs" && {
      translationsForLanguage: czechTranslations,
      dayjsLocaleConfigForLanguage: czechDayjsLocale,
    }),
  });

  return i18n;
};
